import * as ActionTypes from './types.action';
import {updateSideBasketStatus} from './loading.action';
import {setFulfillmentError, showItemErrorAlert, showServerErrorAlert} from './error.action';
import basketApi from '../api/basket.api';
import {
  getValueFromLocalStorage,
  persistValueToLocalStorage,
  removeValueFromLocalStorage
} from '../helpers/localStorage.helper';
import {FOODIT_LOCALSTORAGE, SERVICE_TYPE} from '../constants/constant';
import {changeFormValue} from './form.action';
import {setCheckoutData} from './checkoutData.action';
import {SentryHelper} from '../helpers/sentry.helper';

const updateBasketStore = basket => ({
  type: ActionTypes.UPDATE_BASKET_ITEM_IN_STORE_SUCCESS,
  basket
});

export const updateBasket = (type, restaurantCode, itemOrigin, orderSource) => (dispatch, getState) => {
  const store = getState();
  dispatch(updateSideBasketStatus(true));

  return basketApi.updateBasket(type, restaurantCode, store.basket, store.basketItem, itemOrigin, orderSource)
    .then(basket => {
      persistValueToLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID, basket.uuid);
      dispatch(updateSideBasketStatus(false));

      if (basket.mealChanges) {
        dispatch(showItemErrorAlert(true));
      } else {
        return dispatch(updateBasketStore(basket));
      }
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          type,
          restaurantCode,
          storeBasket: store.basket,
          storeBasketItem: store.basketItem,
          error: error.response && error.response.data
        },
        { method: 'updateBasket' }
      );

      dispatch(showServerErrorAlert(true));
      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

const removeBasketItemSuccess = basket => ({
  type: ActionTypes.REMOVE_BASKET_ITEM_SUCCESS,
  basket
});

export const removeBasketItem = (basketUuid, itemUuid) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.removeBasketItem(basketUuid, itemUuid)
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      return dispatch(removeBasketItemSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          itemUuid,
          error: error.response && error.response.data
        },
        { method: 'removeBasketItem' }
      );

      dispatch(showServerErrorAlert(true));
      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

export const addItemStraightToBasket = (basketUuid, restaurantCode, item, origin, orderSource) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.addItemStraightToBasket(basketUuid, restaurantCode, item, origin, orderSource)
    .then(basket => {
      persistValueToLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID, basket.uuid);
      dispatch(updateSideBasketStatus(false));
      return dispatch(updateBasketStore(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          restaurantCode,
          item,
          error: error.response && error.response.data
        },
        { method: 'addItemStraightToBasket' }
      );

      dispatch(showServerErrorAlert(true));
      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

const updateBasketItemSuccess = basket => ({
  type: ActionTypes.UPDATE_BASKET_ITEM_SUCCESS,
  basket
});

export const updateBasketItem = (basketUuid, itemUuid, updatedFields) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.updateBasketItem(basketUuid, itemUuid, updatedFields)
    .then(basket => {
      dispatch(updateSideBasketStatus(false));

      if (basket.mealChanges) {
        dispatch(showItemErrorAlert(true));
      } else {
        return dispatch(updateBasketItemSuccess(basket));
      }
    })
    .catch(error => {
      if (error.response.status !== 406) { // 406 - basket busy exception
        SentryHelper.logException(new Error(error), {
            basketUuid,
            itemUuid,
            updatedFields,
            error: error.response && error.response.data
          },
          { method: 'updateBasketItem' }
        );

        dispatch(showServerErrorAlert(true));
      }

      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

export const updateBasketItemQuantity = (basketUuid, itemUuid, quantity) => {
  const updatedFields = {
    quantity
  };
  return updateBasketItem(basketUuid, itemUuid, updatedFields);
};

const getBasketSuccess = basket => ({
  type: ActionTypes.GET_BASKET_SUCCESS,
  basket
});

export const getBasket = (basketUuid, restaurantCode) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.getBasket(basketUuid, restaurantCode)
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      return dispatch(getBasketSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          error: error.response && error.response.data
        },
        { method: 'getBasket' }
      );

      if (error.response.status !== 404) {
        dispatch(showServerErrorAlert(true));
      }

      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

export const reorder = (orderId, restaurantCode) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.reorder(orderId, restaurantCode)
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      persistValueToLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID, basket.uuid);
      return dispatch(getBasketSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          orderId,
          error: error.response && error.response.data
        },
        { method: 'reorder' }
      );

      if (error.response.status !== 404) {
        dispatch(showServerErrorAlert(true));
      }

      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

const updateFulfillmentSuccess = basket => ({
  type: ActionTypes.UPDATE_FULFILLMENT_SUCCESS,
  basket
});

export const updateFulfillment = (basketUuid, fulfillmentValues) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  const fulfillmentData = {
    delivery: fulfillmentValues.serviceType === SERVICE_TYPE.DELIVERY
  };

  if (
    fulfillmentValues.serviceType === SERVICE_TYPE.DELIVERY &&
    fulfillmentValues.addressLine1 &&
    fulfillmentValues.city &&
    fulfillmentValues.postcode
  ) {
    fulfillmentData.address = {
      addressLine1: fulfillmentValues.addressLine1,
      addressLine2: fulfillmentValues.city,
      city: fulfillmentValues.city,
      postcode: fulfillmentValues.postcode
    };
  }

  return basketApi.updateFulfillment(basketUuid, fulfillmentData)
    .then(basket => {
      dispatch(setFulfillmentError(false));
      dispatch(updateSideBasketStatus(false));
      if (basket.fulfillment) {
        dispatch(changeFormValue('serviceTypeRadioButtons', 'serviceType', basket.fulfillment.delivery ? SERVICE_TYPE.DELIVERY : SERVICE_TYPE.COLLECTION));
        dispatch(changeFormValue('checkoutForm', 'serviceType', basket.fulfillment.delivery ? SERVICE_TYPE.DELIVERY : SERVICE_TYPE.COLLECTION));
      }
      return dispatch(updateFulfillmentSuccess(basket));
    })
    .catch(error => {
      if (error.response && error.response.status !== 422) { // 422 - postcode not found exception
        SentryHelper.logException(new Error(error), {
            basketUuid,
            fulfillmentData,
            error: error.response && error.response.data
          },
          { method: 'updateFulfillment' }
        );

        dispatch(showServerErrorAlert(true));
      }

      dispatch(updateSideBasketStatus(false));
      throw error;
    });
};

const setBasketConsumerSuccess = basket => ({
  type: ActionTypes.SET_BASKET_CONSUMER_SUCCESS,
  basket
});

export const setBasketConsumer = (basketUuid, consumer) => dispatch => {
  return basketApi.setBasketConsumer(basketUuid, consumer)
    .then(basket => dispatch(setBasketConsumerSuccess(basket)))
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          consumer,
          error: error.response && error.response.data
        },
        { method: 'setBasketConsumer' }
      );

      dispatch(showServerErrorAlert(true));
      throw error;
    });
};

const setBasketFulfillAtSuccess = basket => ({
  type: ActionTypes.SET_BASKET_FULFILLAT_SUCCESS,
  basket
});

export const setBasketFulfillAt = (basketUuid, fulfillAt) => dispatch => {
  dispatch(updateSideBasketStatus(true));
  const fulFillAtTime = fulfillAt ? fulfillAt.format('YYYY-MM-DDTHH:mm') : null;

  return basketApi.setBasketFulfillAt(basketUuid, fulFillAtTime)
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      return dispatch(setBasketFulfillAtSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          fulfillAt,
          error: error.response && error.response.data
        },
        { method: 'setBasketFulfillAt' }
      );

      dispatch(updateSideBasketStatus(false));
      dispatch(showServerErrorAlert(true));
      throw (error);
    });
};

const setPaymentMethodSuccess= basket => ({
  type: ActionTypes.SET_BASKET_CONSUMER_SUCCESS,
  basket
});

export const setPaymentMethod = (basketUuid, paymentMethod) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.setPaymentMethod(basketUuid, {paymentMethod})
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      if (basket.paymentMethod) {
        dispatch(changeFormValue('checkoutForm', 'paymentType', basket.paymentMethod));
      }
      return dispatch(setPaymentMethodSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          paymentMethod,
          error: error.response && error.response.data
        },
        { method: 'setPaymentMethod' }
      );

      dispatch(updateSideBasketStatus(false));
      dispatch(showServerErrorAlert(true));
      throw error;
    });
};

const addVoucherSuccess= basket => ({
  type: ActionTypes.ADD_VOUCHER_BASKET_SUCCESS,
  basket
});

export const addVoucher = (basketUuid, voucher) => dispatch => {
  dispatch(updateSideBasketStatus(true));

  return basketApi.addVoucher(basketUuid, {code: voucher})
    .then(basket => {
      dispatch(updateSideBasketStatus(false));
      return dispatch(addVoucherSuccess(basket));
    })
    .catch(error => {
      SentryHelper.logException(new Error(error), {
          basketUuid,
          voucher,
          error: error.response && error.response.data
        },
        { method: 'addVoucher' }
      );

      dispatch(updateSideBasketStatus(false));
      dispatch(showServerErrorAlert(true));
      throw (error);
    });
};

const deleteBasketSuccess = () => ({
  type: ActionTypes.DELETE_BASKET_SUCCESS
});

export const deleteBasket = () => dispatch => {
  if (!__SERVER__) {
    const storedCheckoutData = getValueFromLocalStorage(FOODIT_LOCALSTORAGE.CHECKOUT_DATA);
    if (storedCheckoutData) {
      const checkoutDataModified = Object.assign({}, storedCheckoutData, {
        paymentType: null,
        serviceType: null
      });

      persistValueToLocalStorage(
        FOODIT_LOCALSTORAGE.CHECKOUT_DATA,
        checkoutDataModified
      );

      dispatch(setCheckoutData(checkoutDataModified));
    }
  }

  dispatch(changeFormValue('checkoutForm', 'paymentType', null));

  removeValueFromLocalStorage(FOODIT_LOCALSTORAGE.BASKET_UUID);
  return dispatch(deleteBasketSuccess());
};



// WEBPACK FOOTER //
// ./actions/basket.action.js